<template>
  <div
    :class="[plan.toLowerCase()]"
    class="plan-card"
  >
    <div class="plan-card__wrapper">
      <img
        :src="`/img/plans/${plan?.toLowerCase()}.png`"
        alt=""
        class="plan-card__img"
        height="44"
        width="44"
      >
      <div class="plan-card__text">
        <h3 class="text__title">
          {{ t(`plans.${plan}.title`) }}
        </h3>
        <p class="text__desc">
          {{ t(`plans.${plan}.desc`) }}
        </p>
      </div>

      <div class="plan-card__price">
        <template v-if="info?.price">
          <UiPrice
            :decimal="2"
            :price="
              period === PlanDurationType.Monthly
                ? info?.price : info?.priceAnnually
            "
          />
          <span> / {{ period === PlanDurationType.Monthly ? t('month') : t('year') }}</span>
        </template>
        <span v-else>
          {{ t('custom_price') }}
        </span>
      </div>

      <button
        v-if="!user?.had_trial_plan && (plan === UserPlan.Pro || plan === UserPlan.Standard)"
        class="btn btn-link btn-link-dashed fw500 px0 green"
        @click="tryTrial(plan)"
      >
        {{ t('try_trial') }}
      </button>

      <button
        :class="{ 'btn-gradient-violet': isPopular }"
        :disabled="plan === UserPlan.Business || plan === user?.plan"
        class="btn btn-primary w-100 fw500 h44"
        @click="choosePlan"
      >
        {{
          t(
            plan === UserPlan.Business ? 'coming_soon'
            : plan === user?.plan ? 'current_plan'
              : user?.plan && user?.plan !== UserPlan.Free ? 'change_plan'
                : 'select_this_plan',
          )
        }}
      </button>

      <div class="plan-card__config">
        <div class="config__item">
          <svg
            class="config__item-icon"
            height="20"
            width="20"
          >
            <use xlink:href="/icons/sprite.svg#i-request" />
          </svg>
          <div class="config__item-right">
            <span class="config__item-right-title">
              {{ info ? Intl.NumberFormat().format(info.maxApiRequestsCount) : t('custom') }}
            </span>
            <p class="config__item-right-subtitle">
              {{ t('api_req_per_month') }}
            </p>
          </div>
        </div>
        <div class="config__item">
          <svg
            class="config__item-icon"
            height="20"
            width="20"
          >
            <use xlink:href="/icons/sprite.svg#i-clock-countdown" />
          </svg>
          <div class="config__item-right">
            <span class="config__item-right-title">
              {{ info ? info.requestsPerMinute : t('custom') }}
            </span>
            <p class="config__item-right-subtitle">
              {{ t('rate_limit_per_minute') }}
            </p>
          </div>
        </div>
        <div class="config__item">
          <svg
            class="config__item-icon"
            height="20"
            width="20"
          >
            <use xlink:href="/icons/sprite.svg#i-apps" />
          </svg>
          <div class="config__item-right">
            <span class="config__item-right-title"> 5 </span>
            <p class="config__item-right-subtitle">
              {{ t('addons_and_services') }}
            </p>
          </div>
        </div>
      </div>

      <svg
        class="plan-card__bg-image"
        height="100"
        width="100"
      >
        <use xlink:href="/icons/sprite.svg#i-csmc-vector" />
      </svg>
    </div>
    <p
      v-if="isPopular"
      class="plan-card__popular"
    >
      {{ t('most_popular_plan') }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import type { PlanInfoLocal } from '~/data/plans';
import { PlanDurationType, UserPlan } from '~/graphql/generated/schema-types';
import { useStore } from '~/store';
import { tryNowTrial } from '~/utils/api';

const props = defineProps<{
  plan: UserPlan
  period: PlanDurationType
  isPopular?: boolean
  info?: PlanInfoLocal
}>();

const emit = defineEmits<{
  (e: 'choosePlan', plan: UserPlan): void
}>();

const { t } = useI18n();
const localePath = useLocalePath();
const { user } = useStore();

function choosePlan() {
  if (props.plan === UserPlan.Business) {
    return;
  }
  emit('choosePlan', props.plan);
}

async function tryTrial(plan: UserPlan) {
  try {
    const { data, errors } = await tryNowTrial(plan as any);

    if (errors?.length || !data) {
      throw new Error(errors?.[0]?.message || 'Failed to select plan');
    }

    window.location.href = localePath('/profile');
  } catch (e: any) {
    EventBus.$emit('notify', {
      success: false,
      msg: e.message,
    });
  }
}
</script>

<style lang="scss" scoped>
.plan-card {
  position: relative;
  z-index: 1;
  max-width: 400px;

  &__wrapper {
    display: flex;
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    border-radius: 16px;
    background: var(--surfaces-low-container);
    width: 100%;
    overflow: hidden;
    position: relative;
    z-index: 1;
    height: 100%;
  }

  .text {
    &__title {
      color: var(--surfaces-on-surface);
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px; /* 133.333% */
      margin-bottom: 8px;
    }
  }

  &__price:deep(.price) {
    color: var(--surfaces-on-surface);
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; /* 116.667% */
  }

  .btn-gradient-violet {
    border-radius: 10px;
    background: linear-gradient(90deg, #FF59C8 0%, #725DF8 100%);
    transition: all 0.3s ease;
    color: #FFFFFF;

    &:hover {
      background: linear-gradient(90deg, #FF59C8 0%, #FF59C8 20%, #725DF8 100%);
    }
  }

  &__config {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .config__item {
    display: flex;
    gap: 12px;

    &-right {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &-title {
        color: var(--surfaces-on-surface);
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 18px;
      }
    }
  }

  &__bg-image {
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
    opacity: 0.05;
  }

  &.standard .plan-card__bg-image {
    top: -100px;
    right: -175px;
    width: 300px;
    height: auto;
    transform: scaleY(-1) rotate(10deg);
    color: var(--esports);
  }

  &.pro .plan-card__bg-image {
    top: -80px;
    right: -70px;
    width: 250px;
    height: auto;
    color: #F8C1FF;
  }

  &.business .plan-card__bg-image {
    top: -60px;
    right: 0;
    width: 220px;
    height: auto;
    color: var(--csmc-updates);
  }

  &__popular {
    border-radius: 0 0 16px 16px;
    padding: 16px 4px 4px;
    background: var(--accent-secondary);
    color: #FFFFFF;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px; /* 128.571% */
    position: relative;
    bottom: 12px;
    @media screen and (max-width: 979px) {
      display: none;
    }
  }
}
</style>
